import { useContext } from 'react'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import { AssetContext } from './SingleAsset'

const MANAGER_TEAMS = ['Governance', 'Manager', 'Supervisor']
const getTeams = (user) => user?.employee?.teams || user?.teams || []

// The custody and location actions shared by the hero's More menu and the
// Overview tab's Location & Custody card. Each one updates the record, then
// patches the page's copy of the asset (including the audit comment the
// server appends) so nothing needs a refetch.
const useAssetActions = () => {
  const { asset, setAsset } = useContext(AssetContext)
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)

  const authorSummary = {
    _id: user._id,
    firstName: user.firstName,
    lastName: user.lastName,
    photograph: user.photograph,
  }

  const localComment = (comment) => ({
    comment,
    author: authorSummary,
    createdAt: new Date(),
    _id: new Date().toISOString(),
  })

  const patch = (body, comment, apply, successMessage, failureMessage) =>
    API.put(`/asset/${asset._id}`, {
      ...body,
      ...(comment && {
        $push: { comments: { comment, author: user._id } },
      }),
    })
      .then((res) => {
        if (res.status === 200) {
          setAsset((prev) => ({
            ...prev,
            ...apply(prev),
            comments: comment
              ? [...(prev.comments ?? []), localComment(comment)]
              : prev.comments,
          }))
          if (successMessage) setToast({ message: successMessage })
          return true
        }
        setToast({ message: failureMessage, severity: 'error' })
        return false
      })
      .catch(() => {
        setToast({ message: failureMessage, severity: 'error' })
        return false
      })

  const checkedOutToMe =
    !!asset.checkedOutTo?._id && asset.checkedOutTo._id === user._id
  const isManager =
    !!user?.security?.isSiteAdmin ||
    MANAGER_TEAMS.some((team) => getTeams(user).includes(team))
  // Anyone can check a free asset out to themselves; only the holder or a
  // manager can bring it back in.
  const canCheckIn = !!asset.checkedOutTo && (checkedOutToMe || isManager)

  const checkOutToMe = () =>
    patch(
      { checkedOutTo: user._id },
      `Asset checked out to ${user.firstName} ${user.lastName}`,
      () => ({ checkedOutTo: { ...authorSummary } }),
      'Asset checked out to you',
      'Unable to check out. Please try again later.',
    )

  const checkIn = () =>
    patch(
      { checkedOutTo: null },
      'Asset checked in',
      () => ({ checkedOutTo: null }),
      'Asset checked in',
      'Unable to check in. Please try again later.',
    )

  const unassignVehicle = () =>
    patch(
      { vehicle: null },
      `Asset unassigned from ${asset.vehicle?.callSign ?? 'vehicle'}`,
      () => ({ vehicle: null }),
      'Asset unassigned from vehicle',
      'Unable to remove from vehicle. Please try again later.',
    )

  // Recorded inside another asset (e.g. a bag) during that asset's make
  // ready check.
  const removeFromParent = () =>
    patch(
      { parentAsset: null },
      `Asset removed from ${asset.parentAsset?.assetID ?? 'parent asset'}`,
      () => ({ parentAsset: null }),
      `Asset removed from ${asset.parentAsset?.assetID ?? 'parent asset'}`,
      'Unable to remove from parent asset. Please try again later.',
    )

  // Shelved in a kit store (Make Ready Stores).
  const removeFromStore = () =>
    patch(
      { store: null },
      `Asset removed from ${asset.store?.name ?? 'kit store'}`,
      () => ({ store: null }),
      `Asset removed from ${asset.store?.name ?? 'kit store'}`,
      'Unable to remove from store. Please try again later.',
    )

  return {
    checkedOutToMe,
    canCheckIn,
    isManager,
    checkOutToMe,
    checkIn,
    unassignVehicle,
    removeFromParent,
    removeFromStore,
  }
}

export default useAssetActions
